type EventsShape = Record<string, Record<string, { name: string; properties: string[] }>>;

const BuildersGlobalProperties = ['user_id', 'account_id', 'workspace_id', 'account_plan_type'] as const;
export type BuildersGlobalProperty = (typeof BuildersGlobalProperties)[number];

export const Events = {
  aiAssistant: {
    opened: {
      name: 'AI Assistant: Opened',
      properties: ['Trigger Source'] as const,
    },
    closed: {
      name: 'AI Assistant: Closed',
      properties: [] as const,
    },
    transferredToTsChat: {
      name: 'AI Assistant: Transferred to TS Chat',
      properties: ['Message Count', 'Had Tool Calls'] as const,
    },
    transferredToTsSupportForm: {
      name: 'AI Assistant: Transferred to TS Support Form',
      properties: ['Message Count', 'Had Tool Calls'] as const,
    },
    transferredToAccountManagement: {
      name: 'AI Assistant: Transferred to Account Management',
      properties: ['Account Id', 'Is Premium', 'Selected Options', 'Message Count', 'Had Tool Calls'] as const,
    },
    suggestionClicked: {
      name: 'AI Assistant: Suggestion Clicked',
      properties: ['Index', 'Click Location'] as const,
    },
    promptCardClicked: {
      name: 'AI Assistant: Prompt Card Clicked',
      properties: ['Title', 'Source', 'Position'] as const,
    },
    newChatClicked: {
      name: 'AI Assistant: New Chat Clicked',
      properties: [] as const,
    },
    viewedHistory: {
      name: 'AI Assistant: Viewed History',
      properties: [] as const,
    },
    historyItemClicked: {
      name: 'AI Assistant: History Item Clicked',
      properties: [] as const,
    },
    historyItemRenamed: {
      name: 'AI Assistant: History Item Renamed',
      properties: [] as const,
    },
    historyItemDeleted: {
      name: 'AI Assistant: History Item Deleted',
      properties: [] as const,
    },
    feedbackLinkClicked: {
      name: 'AI Assistant: Feedback Link Clicked',
      properties: [] as const,
    },
    feedbackFormSubmitted: {
      name: 'AI Assistant: Feedback Form Submitted',
      properties: ['Feedback'] as const,
    },
    referencePaginated: {
      name: 'AI Assistant: Reference Paginated',
      properties: ['Direction', 'Current Index', 'Total References'] as const,
    },
    suggestionsShown: {
      name: 'AI Assistant: Suggestions Shown',
      properties: [] as const,
    },
    suggestionsHidden: {
      name: 'AI Assistant: Suggestions Hidden',
      properties: [] as const,
    },
    editBusinessProfileClicked: {
      name: 'AI Assistant: Edit Business Profile Clicked',
      properties: ['Click Location'] as const,
    },
    contextUsedPopoverOpened: {
      name: 'AI Assistant: Context Used Popover Opened',
      properties: [] as const,
    },
    contextUsedAccordionClicked: {
      name: 'AI Assistant: Context Used Accordion Clicked',
      properties: ['section'] as const,
    },
    tourStarted: {
      name: 'AI Assistant: Tour Started',
      properties: ['Tour Name'] as const,
    },
    tourDismissed: {
      name: 'AI Assistant: Tour Dismissed',
      properties: ['Tour Name'] as const,
    },
    messageSent: {
      name: 'AI Assistant: Message Sent',
      properties: ['Message', 'Has Files', 'File Count', 'Turn Number', 'Is First Message', 'Conversation ID'] as const,
    },
    stopClicked: {
      name: 'AI Assistant: Stop Clicked',
      properties: [] as const,
    },
    messageCopied: {
      name: 'AI Assistant: Message Copied',
      properties: [] as const,
    },
    thumbsUp: {
      name: 'AI Assistant: Thumbs Up',
      properties: [] as const,
    },
    thumbsDown: {
      name: 'AI Assistant: Thumbs Down',
      properties: ['Feedback Text', 'Has Feedback'] as const,
    },
    fileUploaded: {
      name: 'AI Assistant: File Uploaded',
      properties: ['Count', 'File Types'] as const,
    },
    fileDownloaded: {
      name: 'AI Assistant: File Downloaded',
      properties: ['File Type'] as const,
    },
    expandedToFullScreen: {
      name: 'AI Assistant: Expanded to Full Screen',
      properties: [] as const,
    },
    collapsedFromFullScreen: {
      name: 'AI Assistant: Collapsed from Full Screen',
      properties: [] as const,
    },
    applyChangesToggled: {
      name: 'AI Assistant: Apply Changes Toggled',
      properties: ['Enabled'] as const,
    },
    webSearchToggled: {
      name: 'AI Assistant: Web Search Toggled',
      properties: ['Enabled'] as const,
    },
    approvalAllowed: {
      name: 'AI Assistant: Approval Allowed',
      properties: ['Approval Type', 'Tool Name'] as const,
    },
    approvalDenied: {
      name: 'AI Assistant: Approval Denied',
      properties: ['Tool Name'] as const,
    },
    autoApprovalTriggered: {
      name: 'AI Assistant: Auto Approval Triggered',
      properties: ['Tool Name'] as const,
    },
    routineSuggestionClicked: {
      name: 'AI Assistant: Routine Suggestion Clicked',
      properties: ['Title'] as const,
    },
    routineEditClicked: {
      name: 'AI Assistant: Routine Edit Clicked',
      properties: [] as const,
    },
    routineSaved: {
      name: 'AI Assistant: Routine Saved',
      properties: [] as const,
    },
    routinePaused: {
      name: 'AI Assistant: Routine Paused',
      properties: [] as const,
    },
    routinePausedFromEmail: {
      name: 'AI Assistant: Routine Paused From Email',
      properties: [] as const,
    },
    routineResumed: {
      name: 'AI Assistant: Routine Resumed',
      properties: [] as const,
    },
    routineDeleted: {
      name: 'AI Assistant: Routine Deleted',
      properties: [] as const,
    },
    routineToolCreated: {
      name: 'AI Assistant: Routine Tool Created',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolUpdated: {
      name: 'AI Assistant: Routine Tool Updated',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolDeleted: {
      name: 'AI Assistant: Routine Tool Deleted',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolRun: {
      name: 'AI Assistant: Routine Tool Run',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolListed: {
      name: 'AI Assistant: Routine Tool Listed',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolHistory: {
      name: 'AI Assistant: Routine Tool History',
      properties: ['Action', 'Routine Name'] as const,
    },
    tableExported: {
      name: 'AI Assistant: Table Exported',
      properties: [] as const,
    },
    tableCopied: {
      name: 'AI Assistant: Table Copied',
      properties: [] as const,
    },
    limitReached: {
      name: 'AI Assistant: Limit Reached',
      properties: ['Feature', 'Limit', 'Current Count'] as const,
    },
  },
  aiProfile: {
    viewed: {
      name: 'AI Profile: Viewed',
      properties: ['Fields Total', 'Fields AI Populated', 'Fields User Overridden', 'Fields Empty'] as const,
    },
    editIntent: {
      name: 'AI Profile: Edit Intent',
      properties: ['Field'] as const,
    },
    saved: {
      name: 'AI Profile: Saved',
      properties: [
        'Fields Changed Count',
        'Fields Added Count',
        'Fields Edited Count',
        'Fields Cleared Count',
        'Changed Fields',
        'Edit Session Ms',
      ] as const,
    },
    discarded: {
      name: 'AI Profile: Discarded',
      properties: ['Unsaved Fields Changed Count'] as const,
    },
    tonePreviewGetPreviewClicked: {
      name: 'AI Profile: Tone Preview Get Preview Clicked',
      properties: ['Channel', 'Sample Input Customized'] as const,
    },
    tonePreviewCopyOutputClicked: {
      name: 'AI Profile: Tone Preview Copy Output Clicked',
      properties: [] as const,
    },
    tonePreviewShuffleSampleInputClicked: {
      name: 'AI Profile: Tone Preview Shuffle Sample Input Clicked',
      properties: [] as const,
    },
    tonePreviewShowSampleInputToggled: {
      name: 'AI Profile: Tone Preview Show Sample Input Toggled',
      properties: [] as const,
    },
  },
  auditLogs: {
    viewed: {
      name: 'Audit Logs: Viewed',
      properties: ['contextType', 'hasFilters', 'workspaceId'] as const,
    },
    contextChanged: {
      name: 'Audit Logs: Context Changed',
      properties: ['contextType', 'autoSelected'] as const,
    },
    workspaceChanged: {
      name: 'Audit Logs: Workspace Changed',
      properties: ['workspaceId'] as const,
    },
    filtersChanged: {
      name: 'Audit Logs: Filters Changed',
      properties: ['filterCount'] as const,
    },
    exportCreated: {
      name: 'Audit Logs: Export Created',
      properties: ['context', 'contextId', 'startTime', 'endTime', 'objectType'] as const,
    },
    exportFailed: {
      name: 'Audit Logs: Export Failed',
      properties: ['context', 'contextId', 'error'] as const,
    },
  },
  deliverability: {
    viewed: {
      name: 'Deliverability: Viewed',
      properties: ['workspaceId', 'hasUnlimitedAccess'] as const,
    },
  },
  universalSearch: {
    opened: {
      name: 'Universal Search: Opened',
      properties: ['Entry Point'] as const,
    },
    itemSelected: {
      name: 'Universal Search: Item Selected',
      properties: [
        'Item Type',
        'Result Kind',
        'Query',
        'Result Position',
        'Results Count',
        'API Results Count',
        'Open in New Tab',
        'Time to First Result Ms',
        'Active Filter',
        'Active Sub Filter',
        'Filters Open',
        'Preview Open',
      ] as const,
    },
    previewOpened: {
      name: 'Universal Search: Preview Opened',
      properties: ['Result Kind', 'Query'] as const,
    },
    previewClosed: {
      name: 'Universal Search: Preview Closed',
      properties: ['Result Kind', 'Query'] as const,
    },
    filterChanged: {
      name: 'Universal Search: Filter Changed',
      properties: ['Filter Key', 'Filter Value', 'Previous Filter', 'Previous Sub Filter', 'Query'] as const,
    },
    filterVisibilityToggled: {
      name: 'Universal Search: Filter Visibility Toggled',
      properties: ['Visible'] as const,
    },
    closed: {
      name: 'Universal Search: Closed',
      properties: ['Reason', 'Had Query', 'Query', 'Time Open Ms', 'Active Filter', 'Active Sub Filter'] as const,
    },
  },
  customer_io_basics: {
    modal_viewed: {
      name: 'Customer.io Basics: Modal Viewed',
      properties: [] as const,
    },
    video_clicked: {
      name: 'Customer.io Basics: Video Clicked',
      properties: ['id'] as const,
    },
    video_started: {
      name: 'Customer.io Basics: Video Started',
      properties: ['id', 'name', 'watched', 'started', 'total'] as const,
    },
    video_watched: {
      name: 'Customer.io Basics: Video Watched',
      properties: ['id', 'name', 'watched', 'started', 'total'] as const,
    },
    video_stopped: {
      name: 'Customer.io Basics: Video Stopped',
      properties: ['id', 'name', 'pauseTime', 'watched', 'started', 'total'] as const,
    },
    video_time_shifted: {
      name: 'Customer.io Basics: Video Time Shifted',
      properties: ['id', 'name', 'seekTo', 'seekFrom'] as const,
    },
    previous_clicked: {
      name: 'Customer.io Basics: Previous Clicked',
      properties: ['id'] as const,
    },
    next_clicked: {
      name: 'Customer.io Basics: Next Clicked',
      properties: ['id'] as const,
    },
  },
  help_center: {
    opened: {
      name: 'Help Center: Help Menu Opened',
      properties: [] as const,
    },
    learnAcademyClicked: {
      name: 'Help Center: Learn with Academy',
      properties: [] as const,
    },
    askCommunityClicked: {
      name: 'Help Center: Ask the Community',
      properties: [] as const,
    },
    platformDocsClicked: {
      name: 'Help Center: Platform Documentation',
      properties: [] as const,
    },
    apiDocsClicked: {
      name: 'Help Center: API Documentation',
      properties: [] as const,
    },
    releaseNotesClicked: {
      name: 'Help Center: Release Notes',
      properties: [] as const,
    },
    connectWithPartnerClicked: {
      name: 'Help Center: Connect with a Partner',
      properties: [] as const,
    },
    feedbackRequestStarted: {
      name: 'Help Center: Send Us Feedback',
      properties: [] as const,
    },
    helpRequestStarted: {
      name: 'Help Center: Get Help With an Issue',
      properties: [] as const,
    },
    aiAssistantClicked: {
      name: 'Help Center: Chat with AI Assistant',
      properties: [] as const,
    },
    chatWithSupportClicked: {
      name: 'Help Center: Chat with Support',
      properties: [] as const,
    },
    systemStatusClicked: {
      name: 'Help Center: System Status',
      properties: [] as const,
    },
    grantSupportAccessClicked: {
      name: 'Help Center: Set Support Access',
      properties: [] as const,
    },
  },
  inline_support: {
    opened: {
      name: 'Inline Support: Opened',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    closed: {
      name: 'Inline Support: Closed',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    supportAccessGranted: {
      name: 'Inline Support: Support Access Granted',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    supportAccessError: {
      name: 'Inline Support: Support Access Error',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    submitClicked: {
      name: 'Inline Support: Submit Clicked',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    submitError: {
      name: 'Inline Support: Submit Error',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    submitSuccess: {
      name: 'Inline Support: Submit Success',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    supportAccessGuidanceShown: {
      name: 'Inline Support: Support Access Guidance Shown',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
  },
  plan_and_billing: {
    supportRequestStarted: {
      name: 'Plan and Billing: Support Request Started',
      properties: ['accountId', 'workspaceId', 'source'] as const,
    },
    supportRequestCompleted: {
      name: 'Plan and Billing: Support Request Completed',
      properties: ['accountId', 'workspaceId', 'comment', 'source'] as const,
    },
  },
  mainNav: {
    toggleCollapsed: {
      name: 'main-nav',
      properties: ['isCollapsed'] as const,
    },
  },
  leftNav: {
    itemClicked: {
      name: 'Left Nav: Item Clicked',
      properties: ['Nav Item', 'Nav Section', 'Is Nested', 'Parent Item', 'Entry Point'] as const,
    },
    collapsed: {
      name: 'Left Nav: Collapsed',
      properties: ['Trigger', 'Session Nav Opens After Collapse'] as const,
    },
    expanded: {
      name: 'Left Nav: Expanded',
      properties: ['Trigger', 'Time Collapsed Ms'] as const,
    },
  },
  performance: {
    hydraInitialLoad: {
      name: 'Performance: Hydra Initial Load',
      properties: [
        'Account Id',
        'Data Center',
        'Route Name',
        'Has Tried To Override Metric',
        'Hydra Boot Start',
        'Hydra Authenticated',
        'Hydra Child App Load Start',
        'Hydra Time To Authenticated',
        'Hydra Time To Child App Load Start',
      ] as const,
    },
  },
  workspaceHealth: {
    tabChanged: {
      name: 'Workspace Performance: Tab Changed',
      properties: ['Tab'] as const,
    },
    viewAllClicked: {
      name: 'Workspace Performance: View All Clicked',
      properties: ['Destination'] as const,
    },
    drawerOpened: {
      name: 'Workspace Performance: Drawer Opened',
      properties: ['Drawer'] as const,
    },
    viewBreakdownClicked: {
      name: 'Workspace Performance: View Breakdown Clicked',
      properties: [] as const,
    },
    recommendationCarouselClicked: {
      name: 'Workspace Performance: Recommendation Carousel Clicked',
      properties: ['Direction'] as const,
    },
    recommendationClicked: {
      name: 'Workspace Performance: Recommendation Clicked',
      properties: ['Recommendation'] as const,
    },
    notificationClicked: {
      name: 'Workspace Performance: Notification Clicked',
      properties: [] as const,
    },
    notificationLinkClicked: {
      name: 'Workspace Performance: Notification Link Clicked',
      properties: [] as const,
    },
  },
  tasks: {
    paginated: {
      name: 'Tasks: Paginated',
      properties: ['Direction'] as const,
    },
    paginationReset: {
      name: 'Tasks: Pagination Reset',
      properties: [] as const,
    },
    viewChanged: {
      name: 'Tasks: View Changed',
      properties: ['View'] as const,
    },
    filterApplied: {
      name: 'Tasks: Filter Applied',
      properties: ['Filter Key', 'Filter Value'] as const,
    },
    priorityChanged: {
      name: 'Tasks: Priority Changed',
      properties: ['Mode', 'Task Type'] as const,
    },
  },
  home: {
    viewed: {
      name: 'Home: Viewed',
      properties: ['is_builder', 'is_onboarding'] as const,
    },
    recentCardClicked: {
      name: 'Home: Recent Card Clicked',
      properties: ['Resource Type', 'Position', 'Is Create Action'] as const,
    },
    recentActivityClicked: {
      name: 'Home: Recent Activity Clicked',
      properties: ['Resource Type', 'Action'] as const,
    },
    addFundsClicked: {
      name: 'Home: Clicked Add Funds CTA',
      properties: [] as const,
    },
    inviteTeammateClicked: {
      name: 'Home: Invite Teammate Clicked',
      properties: [] as const,
    },
    messageInvadersOpened: {
      name: 'Home: Message Invaders Opened',
      properties: [] as const,
    },
    messageInvadersGameOver: {
      name: 'Home: Message Invaders Game Over',
      properties: ['Score', 'Wave', 'Is New High Score'] as const,
    },
    agentInputSubmitted: {
      name: 'Home: Agent Input Submitted',
      properties: [] as const,
    },
    workspaceActivityGraphEdited: {
      name: 'Home: Workspace Activity Graph Edited',
      properties: ['Channel'] as const,
    },
    clickedAnalysisLink: {
      name: 'Home: Clicked Analysis Link',
      properties: [] as const,
    },
    clickedClassicDashboard: {
      name: 'Home: Clicked Classic Dashboard',
      properties: [] as const,
    },
    welcomeBannerDismissed: {
      name: 'Home: Welcome Banner Dismissed',
      properties: [] as const,
    },
    welcomeBannerCtaClicked: {
      name: 'Home: Welcome Banner CTA Clicked',
      properties: [] as const,
    },
    promptCardsShown: {
      name: 'Home: Prompt Cards Shown',
      properties: ['Titles', 'Personalized'] as const,
    },
    auditLogsClicked: {
      name: 'Home: Audit Logs Clicked',
      properties: [] as const,
    },
  },
  builders: {
    addFundsClicked: {
      name: 'Builders: Add Funds Clicked',
      properties: [...BuildersGlobalProperties, 'page_name'] as const,
    },
    apiKeySaved: {
      name: 'Builders: API Key Saved',
      properties: [...BuildersGlobalProperties, 'page_name', 'service_account_id'] as const,
    },
    domainAdded: {
      name: 'Builders: Domain Added',
      properties: [...BuildersGlobalProperties, 'page_name', 'domain', 'domain_id'] as const,
    },
    domainVerified: {
      name: 'Builders: Domain Verified',
      properties: [...BuildersGlobalProperties, 'domain', 'verification_method'] as const,
    },
    userAdded: {
      name: 'Builders: User Added',
      properties: [
        ...BuildersGlobalProperties,
        'page_name',
        'is_test_user',
        'user_count',
        'method',
        'remaining_spots',
        'has_email',
      ] as const,
    },
    messageSent: {
      name: 'Builders: Message Sent',
      properties: [...BuildersGlobalProperties, 'is_test_send', 'channel', 'message_type'] as const,
    },
    upNextClicked: {
      name: 'Builders: Up Next Clicked',
      properties: [...BuildersGlobalProperties, 'page_name', 'step'] as const,
    },
  },
  workspaceSetup: {
    clickedChannel: {
      name: 'Workspace Setup: Clicked channel',
      properties: ['account_id', 'channel'] as const,
    },
    clickedDataImport: {
      name: 'Workspace Setup: Clicked data import',
      properties: ['account_id'] as const,
    },
    recommendedNextStep: {
      name: 'Workspace Setup: Clicked recommended next step',
      properties: ['account_id', 'step'] as const,
    },
    viewedAddPeople: {
      name: 'Workspace Setup: Viewed add people',
      properties: [] as const,
    },
    clickedAddPeopleAction: {
      name: 'Workspace Setup: Clicked add people action',
      properties: ['account_id', 'actionName'] as const,
    },
  },
  test_delivery_mode: {
    popover_opened: {
      name: 'Test Delivery Mode: Popover Opened',
      properties: ['Route Name'],
    },
    popover_cta_clicked: {
      name: 'Test Delivery Mode: Popover CTA Clicked',
      properties: ['Route Name', 'Action Name'],
    },
  },
  apiKeys: {
    pageViewed: {
      name: 'API Keys: Page Viewed',
      properties: ['Tab', 'Context'] as const,
    },
    serviceAccountCreated: {
      name: 'API Keys: Service Account Created',
      properties: [] as const,
    },
    serviceAccountDeleted: {
      name: 'API Keys: Service Account Deleted',
      properties: [] as const,
    },
    pipelineKeyCreated: {
      name: 'API Keys: Pipeline Key Created',
      properties: ['Source Name'] as const,
    },
    pipelineKeyDeleted: {
      name: 'API Keys: Pipeline Key Deleted',
      properties: ['Source Name'] as const,
    },
    signingKeyRegenerated: {
      name: 'API Keys: Signing Key Regenerated',
      properties: [] as const,
    },
  },
  multiAccount: {
    viewedAccounts: {
      name: 'Multi-Account: User Viewed Accounts',
      properties: ['Session Count'] as const,
    },
    switchedAccounts: {
      name: 'Multi-Account: User Switched Accounts',
      properties: ['Session Count', 'Current Account ID', 'New Account Name'] as const,
    },
    clickedAddAccount: {
      name: 'Multi-Account: User Clicked Add Account',
      properties: ['Session Count'] as const,
    },
  },
  account: {
    verificationReviewInboxLinkClicked: {
      name: 'Account: Verification Review Inbox Link Clicked',
      properties: ['MX Provider', 'Email Domain'] as const,
    },
  },
} as const satisfies EventsShape;

export type IEvents = typeof Events;

export type EventProperties<
  Namespace extends keyof IEvents,
  E extends keyof IEvents[Namespace],
> = IEvents[Namespace][E] extends { properties: readonly (infer P)[] } ? (P extends string ? P : never) : never;
